import React from "react";
import { motion } from "framer-motion";
import { FiUsers, FiActivity, FiHeart, FiHome } from "react-icons/fi";
import {
  BiMusic,
  BiFootball,
  BiPaint,
  BiCode,
  BiCamera,
  BiBook,
} from "react-icons/bi";

const clubs = [
  {
    icon: BiMusic,
    name: "Music & Choir Club",
    desc: "Vocal and instrumental music training, annual concerts and school events.",
  },
  {
    icon: BiPaint,
    name: "Arts & Crafts Club",
    desc: "Creative expression through painting, sculpture, and mixed media art.",
  },
  {
    icon: BiCode,
    name: "Coding & Robotics Club",
    desc: "Programming, robotics, and technology innovation projects.",
  },
  {
    icon: BiCamera,
    name: "Photography Club",
    desc: "Photography skills, darkroom techniques, and creative storytelling.",
  },
  {
    icon: BiBook,
    name: "Debate & Literature Club",
    desc: "Public speaking, debates, and literary appreciation activities.",
  },
  {
    icon: FiUsers,
    name: "Eco & Environment Club",
    desc: "Promoting environmental awareness and sustainable school practices.",
  },
];

const sports = [
  { name: "Cricket", icon: "🏏", level: "Inter-school competitions" },
  { name: "Football", icon: "⚽", level: "District level tournaments" },
  { name: "Basketball", icon: "🏀", level: "School league" },
  { name: "Volleyball", icon: "🏐", level: "Annual tournaments" },
  { name: "Table Tennis", icon: "🏓", level: "School competitions" },
  { name: "Athletics", icon: "🏃", level: "Track & field events" },
  { name: "Badminton", icon: "🏸", level: "School league" },
  { name: "Chess", icon: "♟️", level: "Inter-school meets" },
];

const community = [
  {
    title: "Annual Charity Drive",
    desc: "Students organize fundraising events to support underprivileged communities in Nepal.",
  },
  {
    title: "Environmental Clean-up",
    desc: "Monthly clean-up drives in and around school to promote environmental responsibility.",
  },
  {
    title: "Blood Donation Camp",
    desc: "Annual blood donation camp organized in collaboration with Red Cross Society.",
  },
  {
    title: "Old Age Home Visit",
    desc: "Regular visits to local old age homes to spread joy and provide companionship.",
  },
];

const facilities = [
  {
    title: "Smart Library",
    desc: "10,000+ books, digital resources, e-library access, and dedicated reading rooms.",
    icon: BiBook,
  },
  {
    title: "Sports Complex",
    desc: "Multi-purpose indoor sports hall, cricket ground, and athletics track.",
    icon: BiFootball,
  },
  {
    title: "Science Labs",
    desc: "Three fully-equipped labs — Physics, Chemistry, and Biology with modern instruments.",
    icon: FiActivity,
  },
  {
    title: "Performing Arts Hall",
    desc: "Fully-equipped auditorium for drama, music performances, and school events.",
    icon: BiMusic,
  },
];

const StudentLife = () => {
  return (
    <div>
      {/* Hero */}
      <div className="hero-gradient py-24 text-center relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle, #bfdbfe 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <p className="text-secondary-400 text-sm font-semibold uppercase tracking-widest mb-3">
            Beyond Academics
          </p>
          <h1 className="text-5xl font-serif font-bold text-white mb-4">
            Student Life
          </h1>
          <p className="text-white/70 text-lg max-w-xl mx-auto">
            At Kalauni Kantipur English School, education goes
            beyond textbooks. Discover the vibrant student experience.
          </p>
        </motion.div>
      </div>

      {/* Clubs & Societies */}
      <section id="clubs" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <p className="section-subtitle mb-2">Explore Your Passion</p>
            <h2 className="section-title">Clubs & Societies</h2>
            <p className="text-gray-500 max-w-2xl mx-auto mt-4">
              Our diverse clubs allow students to explore their interests,
              develop new skills, and build lasting friendships.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {clubs.map((club, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-50 border border-gray-100 rounded-2xl p-6 group card-hover"
              >
                <div className="w-12 h-12 bg-primary-50 border border-primary-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary-500 transition-colors">
                  <club.icon className="w-6 h-6 text-primary-500 group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-bold text-primary-500 mb-2">{club.name}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {club.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sports */}
      <section id="sports" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <p className="section-subtitle mb-2">Physical Excellence</p>
            <h2 className="section-title">Sports Programs</h2>
            <p className="text-gray-500 max-w-2xl mx-auto mt-4">
              We offer a wide range of sports programs to promote physical
              fitness, teamwork, and competitive spirit.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {sports.map((sport, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: idx * 0.07 }}
                viewport={{ once: true }}
                className="bg-white border border-gray-100 rounded-2xl p-5 text-center card-hover"
              >
                <div className="text-3xl mb-3">{sport.icon}</div>
                <h4 className="font-bold text-primary-500 text-sm mb-1">
                  {sport.name}
                </h4>
                <p className="text-gray-500 text-xs">{sport.level}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Activities */}
      <section id="community" className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <p className="section-subtitle mb-2">Giving Back</p>
            <h2 className="section-title">Community Activities</h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {community.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: idx % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="bg-primary-50 border border-primary-100 rounded-2xl p-6 flex gap-4"
              >
                <div className="w-10 h-10 bg-primary-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FiHeart className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-primary-500 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Facilities */}
      <section id="facilities" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <p className="section-subtitle mb-2">Our Infrastructure</p>
            <h2 className="section-title">Key Facilities</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {facilities.map((facility, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-gray-100 rounded-2xl p-8 flex gap-5 card-hover"
              >
                <div className="w-14 h-14 bg-secondary-50 border border-secondary-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <facility.icon className="w-7 h-7 text-secondary-600" />
                </div>
                <div>
                  <h3 className="font-bold text-primary-500 text-lg mb-2">
                    {facility.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {facility.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default StudentLife;
